/**
 * Price-matrix cell selection and keyboard focus (HS2-QV3276). Pure and DOM-free so
 * the transitions are unit-testable.
 *
 * The grid is the matrix's line-item rows by vendor columns. Only mapped cells (a
 * matched quote line) are selectable; empty cells and unmatched quote lines are not.
 * Selection is the selected cells' quote-line ids in row-major order, so it survives
 * a matrix reload, and a cell that is unmapped simply drops out.
 *
 * Positions use `row` / `col` indexes, with -1 for the header row and the row-header
 * column: (r, -1) is row r's header and (-1, c) is column c's header.
 */
import type { QuoteMatrix } from '../data/api.js';

export interface MatrixGrid {
  /** Line-item ids, top to bottom. */
  rows: readonly string[];
  /** Quotation ids, left to right. */
  columns: readonly string[];
  /** The quote-line id mapped at (row, col), or null for an empty cell. */
  cells: readonly (readonly (string | null)[])[];
}

export interface MatrixPosition {
  row: number;
  col: number;
}

export type MatrixTarget =
  { kind: 'cell'; row: number; col: number } | { kind: 'row'; row: number } | { kind: 'column'; col: number };

export interface MatrixSelection {
  /** Selected quote-line ids, row-major. */
  selected: readonly string[];
  /** Where a Shift range starts; null before the first selection. */
  anchor: MatrixPosition | null;
}

export interface SelectionModifiers {
  /** Cmd/Ctrl: add to or remove from the selection. */
  toggle: boolean;
  /** Shift: extend from the anchor. */
  range: boolean;
}

export type MatrixDirection = 'up' | 'down' | 'left' | 'right';

export const EMPTY_MATRIX_SELECTION: MatrixSelection = { selected: [], anchor: null };

export function matrixGrid(matrix: Pick<QuoteMatrix, 'lineItems' | 'columns' | 'cells'>): MatrixGrid {
  const rows = matrix.lineItems.map((line) => line.id);
  const columns = matrix.columns.map((column) => column.quotationId);
  return {
    rows,
    columns,
    cells: rows.map((lineId) => columns.map((quotationId) => matrix.cells[lineId]?.[quotationId]?.quoteLineId ?? null)),
  };
}

function cellAt(grid: MatrixGrid, row: number, col: number): string | null {
  return grid.cells[row]?.[col] ?? null;
}

/** Mapped quote-line ids inside the inclusive rectangle, row-major. */
function idsIn(grid: MatrixGrid, rowFrom: number, rowTo: number, colFrom: number, colTo: number): string[] {
  const ids: string[] = [];
  for (let row = Math.max(0, Math.min(rowFrom, rowTo)); row <= Math.max(rowFrom, rowTo); row++) {
    for (let col = Math.max(0, Math.min(colFrom, colTo)); col <= Math.max(colFrom, colTo); col++) {
      const id = cellAt(grid, row, col);
      if (id !== null) ids.push(id);
    }
  }
  return ids;
}

const lastRow = (grid: MatrixGrid): number => grid.rows.length - 1;
const lastCol = (grid: MatrixGrid): number => grid.columns.length - 1;

/** The ids a target covers on its own: one cell, a whole row, or a whole column. */
function targetIds(grid: MatrixGrid, target: MatrixTarget): string[] {
  if (target.kind === 'cell') return idsIn(grid, target.row, target.row, target.col, target.col);
  if (target.kind === 'row') return idsIn(grid, target.row, target.row, 0, lastCol(grid));
  return idsIn(grid, 0, lastRow(grid), target.col, target.col);
}

/** The ids from the anchor to a target: a rectangle for a cell, a band of whole rows
 *  for a row header, a band of whole columns for a column header. */
function rangeIds(grid: MatrixGrid, anchor: MatrixPosition, target: MatrixTarget): string[] {
  if (target.kind === 'row') return idsIn(grid, anchor.row < 0 ? 0 : anchor.row, target.row, 0, lastCol(grid));
  if (target.kind === 'column') return idsIn(grid, 0, lastRow(grid), anchor.col < 0 ? 0 : anchor.col, target.col);
  // A header anchor (row or col -1) stretches the rectangle to that edge.
  return idsIn(grid, anchor.row < 0 ? 0 : anchor.row, target.row, anchor.col < 0 ? 0 : anchor.col, target.col);
}

function targetPosition(target: MatrixTarget): MatrixPosition {
  if (target.kind === 'cell') return { row: target.row, col: target.col };
  if (target.kind === 'row') return { row: target.row, col: -1 };
  return { row: -1, col: target.col };
}

/** Keep `ids` in the grid's row-major order. */
function ordered(grid: MatrixGrid, ids: ReadonlySet<string>): string[] {
  return grid.cells.flat().filter((id): id is string => id !== null && ids.has(id));
}

/** Apply a click or Space on a cell or header, with Cmd/Ctrl and Shift modifiers. */
export function selectMatrixTarget(
  grid: MatrixGrid,
  current: MatrixSelection,
  target: MatrixTarget,
  modifiers: SelectionModifiers,
): MatrixSelection {
  if (modifiers.range && current.anchor !== null) {
    const range = rangeIds(grid, current.anchor, target);
    const selected = modifiers.toggle ? new Set([...current.selected, ...range]) : new Set(range);
    return { selected: ordered(grid, selected), anchor: current.anchor };
  }
  const ids = targetIds(grid, target);
  const anchor = targetPosition(target);
  if (modifiers.toggle) {
    const selected = new Set(current.selected);
    // A row or column toggles as a unit: if it is fully selected it is removed,
    // otherwise its missing cells are added.
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    for (const id of ids) {
      if (allSelected) selected.delete(id);
      else selected.add(id);
    }
    return { selected: ordered(grid, selected), anchor };
  }
  return { selected: ids, anchor };
}

/** Drop selected ids that are no longer mapped (after a reload) and an anchor that
 *  fell outside the grid. */
export function reconcileMatrixSelection(grid: MatrixGrid, current: MatrixSelection): MatrixSelection {
  const selected = ordered(grid, new Set(current.selected));
  const anchor =
    current.anchor !== null && current.anchor.row <= lastRow(grid) && current.anchor.col <= lastCol(grid)
      ? current.anchor
      : null;
  if (selected.length === current.selected.length && anchor === current.anchor) return current;
  return { selected, anchor };
}

/** Whether keyboard focus may rest at a position: a mapped cell, a row header, or a
 *  column header. The top-left corner and empty cells are skipped. */
export function isFocusable(grid: MatrixGrid, position: MatrixPosition): boolean {
  const { row, col } = position;
  if (row < -1 || col < -1 || row > lastRow(grid) || col > lastCol(grid)) return false;
  if (row === -1 && col === -1) return false;
  if (row === -1 || col === -1) return true;
  return cellAt(grid, row, col) !== null;
}

/** The next focusable position in a direction, skipping empty cells; stays put at an edge. */
export function moveMatrixFocus(grid: MatrixGrid, from: MatrixPosition, direction: MatrixDirection): MatrixPosition {
  let rowStep = 0;
  let colStep = 0;
  switch (direction) {
    case 'up':
      rowStep = -1;
      break;
    case 'down':
      rowStep = 1;
      break;
    case 'left':
      colStep = -1;
      break;
    case 'right':
      colStep = 1;
      break;
  }
  for (let row = from.row + rowStep, col = from.col + colStep; ; row += rowStep, col += colStep) {
    if (row < -1 || col < -1 || row > lastRow(grid) || col > lastCol(grid)) return from;
    if (isFocusable(grid, { row, col })) return { row, col };
  }
}

/** Where the matrix's single tab stop sits: the remembered position while it is still
 *  focusable, else the first mapped cell, else the first row header, else the first
 *  column header; null for an empty grid. */
export function matrixTabStop(grid: MatrixGrid, remembered: MatrixPosition | null): MatrixPosition | null {
  if (remembered !== null && isFocusable(grid, remembered)) return remembered;
  for (let row = 0; row <= lastRow(grid); row++) {
    for (let col = 0; col <= lastCol(grid); col++) if (cellAt(grid, row, col) !== null) return { row, col };
  }
  if (grid.rows.length > 0) return { row: 0, col: -1 };
  if (grid.columns.length > 0) return { row: -1, col: 0 };
  return null;
}

/** Stable DOM key for a position (`data-matrix-pos`). */
export function matrixPositionKey(position: MatrixPosition): string {
  return `${String(position.row)}:${String(position.col)}`;
}

export function parseMatrixPosition(key: string | null): MatrixPosition | null {
  const match = /^(-?\d+):(-?\d+)$/.exec(key ?? '');
  return match === null ? null : { row: Number(match[1]), col: Number(match[2]) };
}

/** The selection target at a position: a cell, a row header, or a column header. */
export function targetAt(position: MatrixPosition): MatrixTarget | null {
  if (position.row >= 0 && position.col >= 0) return { kind: 'cell', row: position.row, col: position.col };
  if (position.row >= 0) return { kind: 'row', row: position.row };
  if (position.col >= 0) return { kind: 'column', col: position.col };
  return null;
}
